import { useState, useEffect, useRef } from 'react';

/** Milliseconds before running an action when `delay` is omitted. */
const DEFAULT_ACTION_DELAY_MS = 50;

function delayForAction(action) {
  const d = action?.delay;
  if (d === 0) return 0;
  if (typeof d === 'number' && Number.isFinite(d) && d >= 0) return d;
  return DEFAULT_ACTION_DELAY_MS;
}

/**
 * Action Executor - Executes AI actions sequentially with delays
 */
export function useActionExecutor(actions, onActionComplete, options = {}) {
  const {
    startIndex = 0,
    requireApproval = false,
    allowedActionTypes = null, // null means allow all
  } = options || {};

  const [currentActionIndex, setCurrentActionIndex] = useState(startIndex);
  const [isExecuting, setIsExecuting] = useState(false);
  const [executedActions, setExecutedActions] = useState([]);
  const [isPaused, setIsPaused] = useState(false);
  const [awaitingApproval, setAwaitingApproval] = useState(false);
  const timeoutRef = useRef(null);
  const onCompleteRef = useRef(onActionComplete);
  const actionsRef = useRef(actions);
  const allowedRef = useRef(allowedActionTypes);
  onCompleteRef.current = onActionComplete;
  actionsRef.current = actions;
  allowedRef.current = allowedActionTypes;

  useEffect(() => {
    const actions = actionsRef.current;
    const onActionComplete = (...args) => {
      if (onCompleteRef.current) onCompleteRef.current(...args);
    };
    const allowedActionTypes = allowedRef.current;

    if (!actions || actions.length === 0) {
      setIsExecuting(false);
      return;
    }

    if (currentActionIndex >= actions.length) {
      setIsExecuting(false);
      return;
    }

    if (isPaused) {
      setIsExecuting(false);
      return;
    }

    setIsExecuting(true);
    const currentAction = actions[currentActionIndex];

    // Permission scopes: skip disallowed action types
    if (Array.isArray(allowedActionTypes) && allowedActionTypes.length > 0) {
      if (!allowedActionTypes.includes(currentAction.type)) {
        setExecutedActions(prev => [...prev, { ...currentAction, skipped: true, reason: 'disallowed' }]);
        if (onActionComplete) onActionComplete({ ...currentAction, skipped: true, reason: 'disallowed' }, currentActionIndex);
        setCurrentActionIndex(prev => prev + 1);
        return;
      }
    }

    // Human approval gate
    if (requireApproval) {
      setAwaitingApproval(true);
      setIsExecuting(false);
      return;
    }

    // Execute action with delay
    timeoutRef.current = setTimeout(() => {
      setExecutedActions(prev => [...prev, currentAction]);
      if (onActionComplete) {
        onActionComplete(currentAction, currentActionIndex);
      }
      setCurrentActionIndex(prev => prev + 1);
    }, delayForAction(currentAction));

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [actions?.length, currentActionIndex, isPaused, requireApproval]);

  const reset = () => {
    setCurrentActionIndex(startIndex);
    setExecutedActions([]);
    setIsExecuting(false);
    setIsPaused(false);
    setAwaitingApproval(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const resetTo = (index) => {
    const next = Math.max(0, Math.min(index, actions?.length || 0));
    setCurrentActionIndex(next);
    setExecutedActions(actions?.slice(0, next) || []);
    setIsExecuting(false);
    setIsPaused(false);
    setAwaitingApproval(false);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  const pause = () => {
    setIsPaused(true);
    setIsExecuting(false);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  const resume = () => {
    setIsPaused(false);
  };

  const approveNext = () => {
    if (!actions || actions.length === 0) return;
    if (currentActionIndex >= actions.length) return;
    setAwaitingApproval(false);
    const currentAction = actions[currentActionIndex];
    timeoutRef.current = setTimeout(() => {
      setExecutedActions(prev => [...prev, currentAction]);
      if (onActionComplete) onActionComplete(currentAction, currentActionIndex);
      setCurrentActionIndex(prev => prev + 1);
    }, delayForAction(currentAction));
  };

  const skipNext = () => {
    if (!actions || actions.length === 0) return;
    if (currentActionIndex >= actions.length) return;
    const currentAction = actions[currentActionIndex];
    setAwaitingApproval(false);
    setExecutedActions(prev => [...prev, { ...currentAction, skipped: true, reason: 'user-skip' }]);
    if (onActionComplete) onActionComplete({ ...currentAction, skipped: true, reason: 'user-skip' }, currentActionIndex);
    setCurrentActionIndex(prev => prev + 1);
  };

  return {
    currentAction: actions?.[currentActionIndex] || null,
    executedActions,
    isExecuting,
    isPaused,
    awaitingApproval,
    progress: actions?.length > 0 ? (currentActionIndex / actions.length) * 100 : 0,
    reset,
    resetTo,
    pause,
    resume,
    approveNext,
    skipNext,
    currentActionIndex
  };
}

